import {
  Block,
  ConflictType,
  isReferenceBlock,
  isRichTextBlock,
  isSelectBlock,
  RichTextBlock,
  BlockRelationship,
  areBRsEqual,
  RootBlock,
  BlockRelationshipForHash,
} from '@basehub/data/replicache/index'
import { stateToJson } from '@basehub/tiptap-api-utils'
import { invariant } from '@epic-web/invariant'
import isEqual from 'lodash/isEqual'
import {
  diffArrays,
  DiffItem,
  getReorderTargetFromDiffItem,
  getStatusFromDiffItem,
  richTextComparator,
} from '@basehub/utils/json-diff/index'
import type { JSONContent } from '@tiptap/core'
import { omitDeep } from '@basehub/utils/omit-deep'
import get from 'lodash/get'
import {
  idToSourceBlockId,
  simpleCopyOnWrite,
} from '../block/mutators/copy-on-write'
import { areBlocksCompatible } from '@basehub/data/replicache/schema/block/compare'
import { getIdempotentId } from '@basehub/utils/id/index'

const omitKeys = [
  'children',
  'id',
  'hash',
  'createdAt',
  'updatedAt',
  'deletedAt',
  'lastRevertedAt',
  'isCommitted',
  'sourceBlockId',
  '__rootBlockId',
  'recentlyAddedChildren',
  'transformations',
]

export type BlockWithBR = {
  block: Block
  br: BlockRelationship
}

type MergeResult = {
  value: BlockWithBR | null
  result: 'conflict' | 'merge' | 'add' | 'delete'
  sourceChanged: boolean
  conflicts?: Conflict[]
}

export function mergeBlocks({
  debug,
  sourceRootBlock,
  idSeed,
  now,
  source,
  head,
  commonAncestor,
  resolveConflict,
}: {
  debug?: boolean
  sourceRootBlock: RootBlock
  idSeed: string
  now: string
  source: BlockWithBR | null
  head: BlockWithBR | null
  commonAncestor: BlockWithBR | null
  resolveConflict: (ctx: {
    type: 'block' | 'br' | 'br-three-ways'
    source: BlockWithBR | null
    head: BlockWithBR | null
    richTextNodeIndex?: number
  }) => 'source' | 'head' | 'unresolved'
}): MergeResult {
  if (source && source.block.type === 'root') {
    return postPro({
      result: 'merge',
      value: source,
      sourceChanged: false,
    })
  }

  if (!source) {
    if (!head) {
      return postPro({
        value: null,
        result: 'delete',
        sourceChanged: true,
      })
    }
    if (!commonAncestor) {
      // head block has been created
      return postPro({
        value: head,
        result: 'add',
        sourceChanged: true,
      })
    } else if (blockAndBRAreEqual({ a: head, b: commonAncestor })) {
      return postPro({
        value: null,
        result: 'delete',
        sourceChanged: true,
      })
    } else {
      /**
       * NOTE: although this is a conflict, we'll simplify by ignoring it.
       * If a block is deleted, it'll always win in a conflict battle.
       * If we don't do this, stuff might get overly complicated for us and for the end user.
       * E.g, what if a top-level block is deleted, but then in `main`, a very nested primitive block is modified?
       * Then, we'd make the user choose between keeping the deletion, or... restoring everything up the tree?
       * The difference with Git is that blocks need their parents in order to live. In git, a file can be there by itself.
       */
      return postPro({
        value: null,
        result: 'delete',
        sourceChanged: true,
      })
      // // conflict. source was deleted and head was modified.
      // const resolution = resolveConflict({
      //   type: 'block',
      //   source: null,
      //   head,
      // })
      // switch (resolution) {
      //   case 'unresolved':
      //     return {
      //       value: null,
      //       result: 'conflict',
      //       conflicts: [
      //         {
      //           sourceValue: null,
      //           headValue: head,
      //           commonValue: null,
      //           key: '',
      //           path: [],
      //         },
      //       ],
      //     }
      //   case 'source':
      //     return {
      //       value: null,
      //       result: 'delete',
      //     }
      //   case 'head':
      //     return {
      //       value: head,
      //       result: 'add',
      //     }
      // }
    }
  } else if (!head) {
    if (!commonAncestor) {
      return postPro({
        value: source,
        result: 'merge',
        sourceChanged: false,
      })
    }
    if (blockAndBRAreEqual({ a: source, b: commonAncestor })) {
      return postPro({
        value: null,
        result: 'delete',
        sourceChanged: true,
      })
    } else {
      /**
       * NOTE: although this is a conflict, we'll simplify by ignoring it.
       * If a block is deleted, it'll always win in a conflict battle.
       * If we don't do this, stuff might get overly complicated for us and for the end user.
       * E.g, what if a top-level block is deleted, but then in `main`, a very nested primitive block is modified?
       * Then, we'd make the user choose between keeping the deletion, or... restoring everything up the tree?
       * The difference with Git is that blocks need their parents in order to live. In git, a file can be there by itself.
       */
      return postPro({
        value: null,
        result: 'delete',
        sourceChanged: true,
      })
      // // conflict. head was deleted and source was modified.
      // const resolution = resolveConflict({
      //   type: 'block',
      //   source,
      //   head: null,
      // })
      // switch (resolution) {
      //   case 'unresolved':
      //     return {
      //       value: null,
      //       result: 'conflict',
      //       conflicts: [
      //         {
      //           sourceValue: source,
      //           headValue: null,
      //           commonValue: null,
      //           key: '',
      //           path: [],
      //         },
      //       ],
      //     }
      //   case 'source':
      //     return {
      //       value: source,
      //       result: 'merge',
      //     }
      //   case 'head':
      //     return {
      //       value: null,
      //       result: 'delete',
      //     }
      // }
    }
  } else {
    if (!commonAncestor && source.block.type === 'root') {
      /**
       * Root blocks are not "merged" actually...
       * So we just return the source block.
       */
      return postPro({
        value: source,
        result: 'merge',
        sourceChanged: false,
      })
    }

    if (blockAndBRAreEqual({ a: source, b: head })) {
      return postPro({
        value: source,
        result: 'merge',
        sourceChanged: false,
      })
    } else if (!commonAncestor) {
      // weird case, but can happen in case of "apply suggestion"
      // where we're not worrying about conflicts at all
      // if conflict resolution is provided, then we can go with it, else we'll throw
      const resolution = resolveConflict({
        type: 'block',
        source,
        head,
      })
      if (resolution !== 'unresolved') {
        return postPro({
          value: resolution === 'source' ? source : head,
          result: 'merge',
          sourceChanged: resolution === 'source' ? false : true,
        })
      } else {
        // eslint-disable-next-line no-console
        console.log({ commonAncestor, source, head })
        throw new Error(`commonAncestor should exist by this point`)
      }
    } else if (blockAndBRAreEqual({ a: source, b: commonAncestor })) {
      if (debug) {
        console.log('source matches ancestor, take head')
      }
      return postPro({
        value: head,
        result: 'merge',
        sourceChanged: true,
      })
    } else if (blockAndBRAreEqual({ a: head, b: commonAncestor })) {
      if (debug) {
        console.log('head matches ancestor, take source')
      }
      return postPro({
        value: source,
        result: 'merge',
        sourceChanged: false,
      })
    } else {
      if (debug) {
        console.log('potential conflict')
      }
      // potential conflict. source and head were both modified.
      // we'll try first to merge the changes, and if conflicts arise, we'll handle them.
      const { conflicts: blockConflicts, value: mergedBlock } =
        mergeModifiedBlocksKeyByKey({
          sourceBlock: source.block,
          headBlock: head.block,
          commonAncestorBlock: commonAncestor.block,
          resolveConflict: (ctx) =>
            resolveConflict({
              type: 'block',
              source: { ...source, block: ctx.source },
              head: { ...head, block: ctx.head },
            }),
          preProcessValue: (value, { path, block }) => {
            if (isRichTextBlock(block) && isEqual(path, ['value', 'state'])) {
              // transform the state to JSON
              return stateToJson(value)
            }
            return value
          },
          customMerge: ({ path, sourceBlock, headBlock, commonBlock }) => {
            if (
              isEqual(path, ['value', 'state']) &&
              isRichTextBlock(sourceBlock) &&
              isRichTextBlock(headBlock) &&
              isRichTextBlock(commonBlock)
            ) {
              return {
                ignore: false,
                ...mergeRichTextState({
                  sourceBlock,
                  headBlock,
                  commonBlock,
                  resolveConflict: (ctx) =>
                    resolveConflict({
                      type: 'block',
                      source: { ...source, block: ctx.source },
                      head: { ...head, block: ctx.head },
                      richTextNodeIndex: ctx.richTextNodeIndex,
                    }),
                }),
              }
            } else if (sourceBlock.type !== headBlock.type) {
              // type changed. if any of them is a comment, we stay with the other one
              if (sourceBlock.type === 'comment') {
                return {
                  ignore: false,
                  conflicts: [],
                  value: get(headBlock, path),
                }
              } else if (headBlock.type === 'comment') {
                return {
                  ignore: false,
                  conflicts: [],
                  value: get(sourceBlock, path),
                }
              }

              // todo handle other type conversions gracefully
              throw new Error(
                `unhandled type conversion on merge: ${sourceBlock.type} <-> ${headBlock.type}`
              )
            } else {
              return {
                ignore: true,
                conflicts: [],
                value: undefined,
              }
            }
          },
          ignoredKeys: omitKeys,
          arrayMergeStrategy: ({ path, sourceBlock, headBlock }) => {
            if (
              (isReferenceBlock(sourceBlock) &&
                isReferenceBlock(headBlock) &&
                isEqual(path, ['value', 'targetBlockIds'])) ||
              (isSelectBlock(sourceBlock) &&
                isSelectBlock(headBlock) &&
                isEqual(path, ['value']))
            ) {
              const someIsSingle =
                !sourceBlock.constraints.multiple ||
                !headBlock.constraints.multiple
              /** For single refs, we don't want to concatenate */
              return someIsSingle ? 'isEqual' : 'concatenate'
            }
            return 'concatenate'
          },
        })

      // Now handle BR conflicts
      let brConflicts: Conflict[] = []
      let mergedBr = source.br

      if (!areBRsEqual(source.br, head.br)) {
        // BR changed in both branches
        if (debug) {
          console.log('br changed in both branches')
        }
        if (
          !areBRsEqual(source.br, commonAncestor.br) &&
          !areBRsEqual(head.br, commonAncestor.br)
        ) {
          if (debug) {
            console.log('br changed seemingly everywhere')
          }
          const resolution = resolveConflict({
            type: 'br-three-ways',
            source,
            head,
          })
          switch (resolution) {
            case 'unresolved':
              brConflicts = [
                {
                  sourceValue: source.br,
                  headValue: head.br,
                  commonValue: commonAncestor.br,
                  key: 'br',
                  path: ['br'],
                },
              ]
              break
            case 'head':
              mergedBr = head.br
              break
            case 'source':
              mergedBr = source.br
              break
          }
        } else if (areBRsEqual(source.br, commonAncestor.br)) {
          if (debug) {
            console.log("source matches ancestor, take head's changes")
          }
          // Source matches ancestor, take head's changes
          mergedBr = head.br
        } else if (areBRsEqual(head.br, commonAncestor.br)) {
          if (debug) {
            console.log("head matches ancestor, take source's changes")
          }
          // Head matches ancestor, take source's changes
          mergedBr = source.br
        }
      }

      if (blockConflicts.length > 0 || brConflicts.length > 0) {
        return postPro({
          value: null,
          result: 'conflict',
          conflicts: [...blockConflicts, ...brConflicts],
          sourceChanged: false,
        })
      } else {
        return postPro({
          value: {
            block: mergedBlock,
            br: mergedBr,
          },
          result: 'merge',
          sourceChanged: true,
        })
      }
    }
  }

  function postPro(mergeResult: MergeResult) {
    if (mergeResult.value && mergeResult.value.block.type !== 'root') {
      const sourceBlock = source?.block || head?.block
      const sourceBr = source?.br || head?.br
      invariant(sourceBlock, `sourceBlock not found`)
      invariant(sourceBr, `sourceBr not found`)
      const mergedBlock: Block = {
        ...mergeResult.value.block,
        sourceBlockId: idToSourceBlockId(sourceBlock),
        id: sourceBlock.id,
        isCommitted: sourceBlock.isCommitted,
        createdAt: sourceBlock.createdAt,
        updatedAt: now,
      }

      let block = mergedBlock

      const shouldCoW =
        mergedBlock.isCommitted ||
        sourceBr.isInlineSuggestion !== sourceRootBlock.isInlineSuggestion ||
        sourceBr.branchId !== sourceRootBlock.branchId

      if (shouldCoW) {
        const corMergedBlock = simpleCopyOnWrite({
          brOrBlock: mergedBlock,
          idSeed: getIdempotentId({
            idSeed: idSeed + sourceRootBlock.id + mergedBlock.id,
          }),
          isoNow: now,
          rootBlock: sourceRootBlock,
        })
        mergeResult.value.block = corMergedBlock
        block = corMergedBlock
      } else {
        mergeResult.value.block = mergedBlock
      }

      const mergedBr: BlockRelationship = {
        ...sourceBr,
        blockId: block.id,
        parentId: mergeResult.value.br.parentId,
        order: mergeResult.value.br.order,
      }

      if (shouldCoW) {
        const corMergedBr = simpleCopyOnWrite({
          brOrBlock: mergedBr,
          idSeed: getIdempotentId({
            idSeed: idSeed + sourceRootBlock.id + mergedBr.id,
          }),
          isoNow: now,
          rootBlock: sourceRootBlock,
          extras: {
            branchId: sourceRootBlock.branchId,
            ...(sourceBr.isInlineSuggestion &&
            !sourceRootBlock.isInlineSuggestion
              ? { isInlineSuggestion: null }
              : {}),
          },
        })
        mergeResult.value.br = corMergedBr
      } else {
        mergeResult.value.br = mergedBr
      }
    }

    return mergeResult
  }
}

function blocksAreEqual({ a, b }: { a: Block; b: Block }): boolean {
  return isEqual(omitDeep(a, omitKeys), omitDeep(b, omitKeys))
}

function blockAndBRAreEqual({
  a,
  b,
}: {
  a: BlockWithBR
  b: BlockWithBR
}): boolean {
  if (!areBlocksCompatible(a.block, b.block)) return false
  /**
   * Now, BRs are a bit trickier.
   * They can be different cause they target a different blockId,
   * but if that block is the same, we can consider the BRs equal.
   * Why can they target different blocks if they're equal? Because, one might be working and the other committed.
   */
  return areBRsEqual(a.br, b.br)
}

type Conflict = {
  sourceValue: any
  headValue: any
  commonValue: any
  key: string
  path: string[]
}

function mergeModifiedBlocksKeyByKey({
  sourceBlock,
  headBlock,
  commonAncestorBlock,
  resolveConflict,
  preProcessValue = (value) => value,
  customMerge,
  ignoredKeys,
  arrayMergeStrategy,
}: {
  sourceBlock: Block
  headBlock: Block
  commonAncestorBlock: Block
  arrayMergeStrategy:
    | 'concatenate'
    | 'isEqual'
    | ((ctx: {
        sourceBlock: Block
        headBlock: Block
        commonBlock: Block
        key: string
        path: string[]
      }) => 'concatenate' | 'isEqual')
  resolveConflict: (ctx: {
    source: Block
    head: Block
    common: Block
    key: string
    path: string[]
  }) => 'source' | 'head' | 'unresolved'
  preProcessValue?: (
    value: any,
    ctx: {
      block: Block
      key: string
      path: string[]
    }
  ) => any
  customMerge?: (ctx: {
    sourceBlock: Block
    headBlock: Block
    commonBlock: Block
    key: string
    path: string[]
  }) => {
    ignore: boolean
    value: any
    conflicts: Conflict[]
  }
  ignoredKeys?: string[]
}): {
  value: Block
  conflicts: Conflict[]
} {
  const conflicts: Conflict[] = []

  // recursive
  function processLevel({
    head,
    source,
    common,
    path,
  }: {
    head: Record<string, any>
    source: Record<string, any>
    common: Record<string, any>
    path: string[]
  }): Record<string, any> {
    const allKeys = new Set([
      ...Object.keys(source),
      ...Object.keys(head),
      ...Object.keys(common),
    ])
    const value: Record<string, any> = {}

    allKeys.forEach((key) => {
      const currentPath = [...path, key]
      const sourceValue = preProcessValue(source[key], {
        block: sourceBlock,
        key,
        path: currentPath,
      })
      const headValue = preProcessValue(head[key], {
        block: headBlock,
        key,
        path: currentPath,
      })
      const commonValue = preProcessValue(common[key], {
        block: commonAncestorBlock,
        key,
        path: currentPath,
      })

      if (ignoredKeys?.includes(key)) {
        value[key] = headValue ?? sourceValue
        return
      }

      if (customMerge) {
        const result = customMerge({
          sourceBlock,
          headBlock,
          commonBlock: commonAncestorBlock,
          key,
          path: currentPath,
        })
        if (!result.ignore) {
          value[key] = result.value
          conflicts.push(...result.conflicts)
          return
        }
      }

      if (Array.isArray(sourceValue) && Array.isArray(headValue)) {
        const strategy =
          typeof arrayMergeStrategy === 'function'
            ? arrayMergeStrategy({
                sourceBlock,
                headBlock,
                commonBlock: commonAncestorBlock,
                key,
                path: currentPath,
              })
            : arrayMergeStrategy
        if (
          strategy === 'concatenate' &&
          typeof sourceValue[0] !== 'object' &&
          typeof headValue[0] !== 'object'
        ) {
          value[key] = Array.from(new Set([...sourceValue, ...headValue]))
          return
        }
      } else if (
        typeof sourceValue === 'object' &&
        sourceValue !== null &&
        typeof headValue === 'object' &&
        headValue !== null
      ) {
        value[key] = processLevel({
          source: sourceValue || {},
          head: headValue || {},
          common: commonValue || {},
          path: currentPath,
        })
        return
      }

      if (isEqual(sourceValue, headValue)) {
        value[key] = sourceValue
      } else if (isEqual(sourceValue, commonValue)) {
        value[key] = headValue
      } else if (isEqual(headValue, commonValue)) {
        value[key] = sourceValue
      } else {
        const resolution = resolveConflict({
          source: sourceBlock,
          head: headBlock,
          common: commonAncestorBlock,
          key,
          path: currentPath,
        })

        switch (resolution) {
          case 'unresolved':
            conflicts.push({
              sourceValue,
              headValue,
              commonValue,
              key,
              path: currentPath,
            })
            break
          case 'head':
            value[key] = headValue
            break
          case 'source':
            value[key] = sourceValue
            break
        }
        return
      }
    })

    return value
  }

  const value = processLevel({
    source: sourceBlock,
    head: headBlock,
    common: commonAncestorBlock,
    path: [],
  })

  return {
    conflicts,
    value: value as Block,
  }
}

/**
 * Merge arrays, deduping by item key and attempting to preserve the best effort order
 * by interleaving elements from source and head arrays.
 */
export function mergeArrayBestEffortOrder<T, X = T>({
  source,
  head,
  getItemKey,
  getProcessedItem = ({ item }) => item as unknown as X,
}: {
  source: T[]
  head: T[]
  getItemKey: (ctx: { item: T; type: 'source' | 'head' }) => string | null
  getProcessedItem?: (ctx: { item: T; type: 'source' | 'head' }) => X
}): X[] {
  // Create a map to keep track of items by their keys to prevent duplicates
  const itemMap = new Map<string, { type: 'source' | 'head'; item: X }>()

  // Helper function to process an item and add it to the map
  const processItem = (item: T, type: 'source' | 'head') => {
    const key = getItemKey({ item, type })
    if (!key) return
    const current = itemMap.get(key)
    if (
      !current ||
      // prioritize source arbitrarily
      (current.type === 'head' && type === 'source')
    ) {
      itemMap.set(key, { type, item: getProcessedItem({ item, type }) })
    }
  }

  // Get the length of the longer array
  const maxLength = Math.max(source.length, head.length)

  // Process items from both arrays in an interleaved manner
  for (let i = 0; i < maxLength; i++) {
    const sourceItem = source[i]
    const headItem = head[i]
    if (sourceItem) {
      processItem(sourceItem, 'source')
    }
    if (headItem) {
      processItem(headItem, 'head')
    }
  }

  // Collect all values from the map and return them
  return Array.from(itemMap.values()).map(({ item }) => item)
}

/**
 * Mostly for sutfacing conflicts in the client side diff UI.
 * Compares sourceBlock and headBlock and returns the status.
 */
export function getBlockConflict({
  sourceBR,
  sourceBlock,
  headBR,
  headBlock,
  commonAncestorBR,
  commonAncestorBlock,
  resolveConflict,
}: {
  sourceBR: BlockRelationshipForHash | null
  sourceBlock: Block | null
  headBR: BlockRelationshipForHash | null
  headBlock: Block | null
  commonAncestorBR: BlockRelationshipForHash | null
  commonAncestorBlock: Block | null
  resolveConflict: (ctx: {
    source: Block | null
    head: Block | null
    richTextNodeId?: string
  }) => 'source' | 'head' | 'unresolved'
}): {
  type: ConflictType
  conflictPaths: string[]
  conflictValues: unknown[]
} | null {
  if (!sourceBlock) {
    if (!headBlock) return null
    if (
      commonAncestorBlock &&
      blocksAreEqual({ a: headBlock, b: commonAncestorBlock }) === false
    ) {
      /**
       * NOTE: although this is a conflict, we'll simplify by ignoring it.
       * If a block is deleted, it'll always win in a conflict battle.
       * If we don't do this, stuff might get overly complicated for us and for the end user.
       * E.g, what if a top-level block is deleted, but then in `main`, a very nested primitive block is modified?
       * Then, we'd make the user choose between keeping the deletion, or... restoring everything up the tree?
       * The difference with Git is that blocks need their parents in order to live. In git, a file can be there by itself.
       */
      return null
      // // conflict. sourceBlock was deleted and headBlock was modified.
      // return {
      //   type: 'deleted-in-source',
      //   conflictPaths: [],
      //   conflictValues: [],
      // }
    }
  } else if (!headBlock) {
    if (
      commonAncestorBlock &&
      blocksAreEqual({ a: sourceBlock, b: commonAncestorBlock }) === false
    ) {
      /**
       * NOTE: although this is a conflict, we'll simplify by ignoring it.
       * If a block is deleted, it'll always win in a conflict battle.
       * If we don't do this, stuff might get overly complicated for us and for the end user.
       * E.g, what if a top-level block is deleted, but then in `main`, a very nested primitive block is modified?
       * Then, we'd make the user choose between keeping the deletion, or... restoring everything up the tree?
       * The difference with Git is that blocks need their parents in order to live. In git, a file can be there by itself.
       */
      return null
      // // conflict. headBlock was deleted and sourceBlock was modified.
      // return { type: 'deleted-in-head', conflictPaths: [], conflictValues: [] }
    }
  } else {
    if (
      blocksAreEqual({ a: sourceBlock, b: headBlock }) === false &&
      commonAncestorBlock &&
      blocksAreEqual({ a: sourceBlock, b: commonAncestorBlock }) === false &&
      blocksAreEqual({ a: headBlock, b: commonAncestorBlock }) === false
    ) {
      // potential conflict. sourceBlock and headBlock were both modified.
      const { conflicts } = mergeModifiedBlocksKeyByKey({
        sourceBlock,
        headBlock,
        commonAncestorBlock,
        resolveConflict,
        preProcessValue: (value, { path, block }) => {
          if (isRichTextBlock(block) && isEqual(path, ['value', 'state'])) {
            // transform the state to JSON
            return stateToJson(value)
          }
          return value
        },
        customMerge: ({ path, sourceBlock, headBlock, commonBlock }) => {
          if (
            isEqual(path, ['value', 'state']) &&
            isRichTextBlock(sourceBlock) &&
            isRichTextBlock(headBlock) &&
            isRichTextBlock(commonBlock)
          ) {
            return {
              ignore: false,
              ...mergeRichTextState({
                sourceBlock,
                headBlock,
                commonBlock,
                resolveConflict,
              }),
            }
          } else {
            return {
              ignore: true,
              conflicts: [],
              value: undefined,
            }
          }
        },
        ignoredKeys: omitKeys,
        arrayMergeStrategy: ({ path, sourceBlock, headBlock }) => {
          if (
            (isReferenceBlock(sourceBlock) &&
              isReferenceBlock(headBlock) &&
              isEqual(path, ['value', 'targetBlockIds'])) ||
            (isSelectBlock(sourceBlock) &&
              isSelectBlock(headBlock) &&
              isEqual(path, ['value']))
          ) {
            const someIsSingle =
              !sourceBlock.constraints.multiple ||
              !headBlock.constraints.multiple
            /** For single refs, we don't want to concatenate */
            return someIsSingle ? 'isEqual' : 'concatenate'
          }
          return 'concatenate'
        },
      })
      if (conflicts.length > 0) {
        return {
          type: 'conflict',
          conflictPaths: conflicts.map((c) => c.path.join('.')),
          conflictValues: conflicts.map((c) => ({
            source: c.sourceValue,
            head: c.headValue,
            common: c.commonValue,
          })),
        }
      }
    }
    // now compare BRs
    invariant(sourceBR, 'sourceBR is required')
    invariant(headBR, 'headBR is required')
    if (
      commonAncestorBR &&
      areBRsEqual(sourceBR, headBR) === false &&
      areBRsEqual(sourceBR, commonAncestorBR) === false &&
      areBRsEqual(headBR, commonAncestorBR) === false
    ) {
      // BR changed in both branches

      return {
        type: 'reorder-in-both',
        conflictPaths: ['br'],
        conflictValues: [sourceBR, headBR, commonAncestorBR],
      }
    }
  }

  return null
}

/* -------------------------------------------------------------------------------------------------
 * Rich Text Helpers
 * -----------------------------------------------------------------------------------------------*/

function mergeRichTextState({
  sourceBlock,
  headBlock,
  commonBlock,
  resolveConflict,
}: {
  sourceBlock: RichTextBlock
  headBlock: RichTextBlock
  commonBlock: RichTextBlock
  resolveConflict: (ctx: {
    source: RichTextBlock
    head: RichTextBlock
    common: RichTextBlock
    richTextNodeIndex: number
  }) => 'source' | 'head' | 'unresolved'
}): {
  value: string
  conflicts: Conflict[]
} {
  const sourceJson = stateToJson(sourceBlock.value.state)
  const headJson = stateToJson(headBlock.value.state)
  const commonJson = stateToJson(commonBlock.value.state)

  const sourceContent = sourceJson?.content ?? []
  const headContent = headJson?.content ?? []
  const commonContent = commonJson?.content ?? []

  const sourceDiff = diffRichTextContent(commonContent, sourceContent)
  const headDiff = diffRichTextContent(commonContent, headContent)

  const result: Array<JSONContent> = []
  const conflicts: Array<Conflict> = []

  commonContent.map((commonNode, index) => {
    const sourceDiffItem =
      sourceDiff.diffResultWithNormalizedIndexesToPreviousNodes[index]
    const headDiffItem =
      headDiff.diffResultWithNormalizedIndexesToPreviousNodes[index]

    const sourceStatus = getStatusFromDiffItem(sourceDiffItem)
    const headStatus = getStatusFromDiffItem(headDiffItem)

    const sourceNodeCurrentIndex =
      sourceDiff.diffResultWithNormalizedIndexesToCurrentNodes.findIndex(
        (item) => item._index === sourceDiffItem?._index
      )
    const headNodeCurrentIndex =
      headDiff.diffResultWithNormalizedIndexesToCurrentNodes.findIndex(
        (item) => item._index === headDiffItem?._index
      )
    const sourceNode = sourceContent[sourceNodeCurrentIndex]
    const headNode = headContent[headNodeCurrentIndex]

    const resolvedIndex = Math.max(sourceNodeCurrentIndex, headNodeCurrentIndex)

    let resolution: 'source' | 'head' | 'unresolved' | 'common' = 'unresolved'

    if (headStatus === 'unmodified' && sourceStatus === 'unmodified') {
      resolution = 'common'
    } else if (
      headStatus !== 'unmodified' &&
      sourceStatus !== 'unmodified' &&
      isEqual(sourceDiffItem, headDiffItem) === false
    ) {
      // possible conflict
      resolution = resolveConflict({
        source: sourceBlock,
        head: headBlock,
        common: commonBlock,
        richTextNodeIndex: resolvedIndex,
      })
    } else {
      // no conflict, we'll take the non-unmodified node
      if (sourceStatus !== 'unmodified') {
        resolution = 'source'
      } else if (headStatus !== 'unmodified') {
        resolution = 'head'
      }
    }

    switch (resolution) {
      case 'common':
        result.push(commonNode)
        break
      case 'unresolved': {
        conflicts.push({
          commonValue: commonNode,
          sourceValue: sourceNode,
          headValue: headNode,
          key: resolvedIndex.toString(10),
          path: ['value', 'state', String(resolvedIndex)],
        })
        break
      }
      case 'source': {
        switch (sourceStatus) {
          case 'removed':
            break
          case 'modified': {
            invariant(sourceNode, 'sourceNode is required')
            result.splice(sourceNodeCurrentIndex, 0, sourceNode)
            break
          }
          case 'reordered': {
            const targetIndex = getReorderTargetFromDiffItem(sourceDiffItem)
            invariant(targetIndex, `targetIndex should be defined by now`)
            invariant(sourceNode, 'sourceNode is required')
            result.splice(targetIndex, 0, sourceNode)
            break
          }
        }
        break
      }
      case 'head': {
        switch (headStatus) {
          case 'removed':
            break
          case 'modified': {
            invariant(headNode, 'headNode is required')
            result.splice(headNodeCurrentIndex, 0, headNode)
            break
          }
          case 'reordered': {
            const targetIndex = Number(headDiffItem?.[0].replace('->', ''))
            invariant(isNaN(targetIndex) === false, 'targetIndex is required')
            invariant(headNode, 'headNode is required')
            result.splice(targetIndex, 0, headNode)
            break
          }
        }
        break
      }
      default:
        invariant(false, 'invalid resolution')
    }
  })

  // processed insertions
  const maxLength = Math.max(
    sourceDiff.diffResult.length,
    headDiff.diffResult.length
  )
  for (let i = 0; i < maxLength; i++) {
    const sourceDiffItem = sourceDiff.diffResult[i]
    const headDiffItem = headDiff.diffResult[i]
    const sourceStatus = getStatusFromDiffItem(sourceDiffItem)
    const headStatus = getStatusFromDiffItem(headDiffItem)

    const shouldDeduplicate = isEqual(sourceDiffItem, headDiffItem)

    if (sourceStatus === 'added') {
      const deletesBeforeThis = sourceDiff.diffResult
        .slice(0, i)
        .filter((d) => getStatusFromDiffItem(d) === 'removed').length
      const nodeIndex = i - deletesBeforeThis
      const node = sourceContent[nodeIndex]
      invariant(node, 'node is required')
      result.splice(nodeIndex, 0, node)
    }

    if (!shouldDeduplicate && headStatus === 'added') {
      const deletesBeforeThis = headDiff.diffResult
        .slice(0, i)
        .filter((d) => getStatusFromDiffItem(d) === 'removed').length
      const nodeIndex = i - deletesBeforeThis
      const node = headContent[nodeIndex]
      invariant(node, 'node is required')
      result.splice(nodeIndex, 0, node)
    }
  }

  return {
    value: JSON.stringify({ ...commonJson, content: result }),
    conflicts,
  }
}

export function diffRichTextContent(a: JSONContent[], b: JSONContent[]) {
  const diffResult = diffArrays(a, b, {
    reorders: { enabled: false },
    customComparator: richTextComparator,
  })

  return {
    diffResult,
    ...getDiffResultsWithNormalizedIndexes(diffResult),
  }
}

export function getDiffResultsWithNormalizedIndexes(diffResult: DiffItem[]) {
  /**
   * Pre build these for fast matching with current/previous' nodes.
   * With these i'll be able to do findIndex and get the index of the diff item on the nodes array.
   * To normalize towards "previous", we remove additions and add reorders in origin.
   * To normalize towards "current", we remove deletions and add reorders in target.
   * modified/unmodified are fine as they are.
   */
  const diffResultWithNormalizedIndexesToPreviousNodes: DiffItem[] = []
  const diffResultWithNormalizedIndexesToCurrentNodes: DiffItem[] = []
  diffResult.forEach((item) => {
    const status = getStatusFromDiffItem(item)
    if (status === 'added') {
      diffResultWithNormalizedIndexesToCurrentNodes.push(item)
    } else if (status === 'removed') {
      diffResultWithNormalizedIndexesToPreviousNodes.push(item)
    } else if (status === 'reordered') {
      const targetIndex = getReorderTargetFromDiffItem(item)
      invariant(
        typeof targetIndex === 'number',
        `targetIndex should be defined by now`
      )
      diffResultWithNormalizedIndexesToCurrentNodes.splice(targetIndex, 0, item)
      diffResultWithNormalizedIndexesToPreviousNodes.push(item)
    } else {
      diffResultWithNormalizedIndexesToCurrentNodes.push(item)
      diffResultWithNormalizedIndexesToPreviousNodes.push(item)
    }
  })

  return {
    diffResultWithNormalizedIndexesToPreviousNodes,
    diffResultWithNormalizedIndexesToCurrentNodes,
  }
}
